import { joinClasses } from 'utils/componentStyles/style.utils';

/**
 * React Select has multiple styled parts that can change based on its state.
 * This object defines the functions used to style each part of the component based on its state (e.g. focused, disabled, selected, etc.).
 * Each key corresponds to a specific part of the React Select component, and the value is a function that takes the current state as an argument
 * and returns a string of class names to apply to that part.
 */
export const baseClassNames = {
  // Styles the outermost wrapper React Select renders around the entire component.
  container: () => 'w-100',

  // Styles the visible select control containing the selected value, input, and indicators.
  control: ({ isDisabled, isFocused }) =>
    joinClasses(
      'd-flex bg-base text-theme rounded-3 border',
      isFocused ? 'border-theme-strong' : 'border-theme',
      isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    ),

  // Styles the area inside the control containing the placeholder, search input, or selected values.
  valueContainer: () => 'px-2 py-1 gap-1',

  // Styles the text input used when searching options.
  input: () => 'text-theme',

  // Styles the currently selected option's label in a single-select.
  singleValue: () => 'text-theme',

  // Styles the placeholder displayed when no option is selected.
  placeholder: () => 'text-theme-muted',

  // Styles the container holding the clear, loading, and dropdown indicators.
  indicatorsContainer: () => 'px-1',

  // Styles the dropdown arrow displayed on the right side of the control.
  dropdownIndicator: () => 'p-1 text-theme cursor-pointer',

  // Styles the clear button displayed when isClearable is enabled.
  clearIndicator: () => 'p-1 text-theme cursor-pointer',

  // Styles the vertical divider between the value and indicator controls.
  indicatorSeparator: () => 'bg-secondary',

  // Styles the dropdown menu container displayed beneath the control.
  // `base-select-menu` / `base-select-option` are the theme hooks: the menu is portalled to
  // `document.body`, so contrast themes cannot reach it through a feature's wrapper class.
  // See the `body[data-theme]` contrast rules in global.scss.
  menu: () => 'base-select-menu mt-1 bg-base border border-theme rounded-3 shadow overflow-y-auto',

  // Styles the scrollable list containing all available dropdown options.
  menuList: () => 'p-1',

  // Styles each individual option displayed inside the dropdown menu.
  option: ({ isDisabled, isFocused, isSelected }) =>
    joinClasses(
      'base-select-option px-2 py-2 rounded-3',
      isSelected ? 'bg-primary-highlight' : isFocused ? 'bg-surface-elevated' : 'bg-surface-panel',
      isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    ),

  // Styles the message displayed when no options match the current search.
  noOptionsMessage: () => 'p-2 text-secondary',

  // Styles each selected-option chip displayed in a multi-select.
  multiValue: () => 'd-flex align-items-center bg-primary-highlight rounded',

  // Styles the text label inside a selected multi-select chip.
  multiValueLabel: () => 'px-2 py-1 text-theme',

  // Styles the remove button inside a selected multi-select chip.
  multiValueRemove: ({ isFocused }) =>
    joinClasses('px-1 cursor-pointer', isFocused && 'bg-surface-elevated'),
};
